// janus-engine/charges/charge-adjust.ts — Lane ROLLS S3 (2026-09-27): the pure resolvers for the GM / sheet CHARGE tools.
//
// Each resolver is a legacy charge writer with its clamp COPIED, run against the caster row the page holds:
//   adjust    — adjust_charge_inc.php → adjust_charges() (janus/encounter/adjust_charges.php): a delta, clamped to [0, max] with
//               max ≤ 0 read as 1, the Orb Slots ceiling (FT_ORB_QUEUE_MAX_SLOTS — current IS this combat's capacity) and the
//               upcast projectile-pool rule (a spend is bounded by the pool itself when it sits above its compiled ceiling — the
//               page cannot prove a pool is one, so that case hands back).
//   set       — change_charges_inc.php → change_charges() (functions.php): an absolute value, floored at 0 and clamped to max ONLY
//               when max is numeric (an unresolved 'NA' max must not clamp the pool to 0).
//   inventory — change_inventory_item_charges_inc.php → change_inventory_charges(): an INVENTORY row's own pool (a wand), max from
//               the bucket-aware `item_charges` layer (the raw-scalar read once clamped every wand to 0).
// The answer is {before, after}; options/tools/charges/charge_truth_commit_inc.php CASes `before` against the `charges` row under a
// row lock (the charges-before contract Lane A's ability commit uses), writes `after`, and checks shape + bounds only.

export const CHARGE_SCHEMA = 'janus.charge.truth.v1';
/** ClassMechanic `orb_slots` (the Machinist queue) and adjust_charges()' override ceiling for it. */
export const ORB_SLOTS_ID = 'DADEF475-1E69-4EF4-8B46-06F3FC2B450D';
export const ORB_QUEUE_MAX_SLOTS = 10;
/** ft_caster_charge_bucket_types(): AbilityType::getAll() + innate_spell_per_long_rest, in layer_entries() scan order. */
export const CHARGE_BUCKETS = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability',
  'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta',
  'sanguine', 'ability_choice', 'innate_spell_per_long_rest'] as const;

import { setRowAbilityCharges } from '../ability/charges/charge-entry.js';

type Row = Record<string, unknown>;
export interface ChargeReaders {
  /** layer_entries() twin (ftLayerEntries). */
  entries: (row: Row | null, key: string) => unknown[];
  /** layer_value() twin (ftLayerValue). */
  value: (row: Row | null, key: string) => unknown;
  /** The catalog definition of an item id the page holds, or null (the page cannot tell). */
  definition?: (id: string) => Row | null;
  /** ft_projectile_pool() twin: is this ability an upcast projectile volley granted by its spell? null = the page cannot tell. */
  projectilePool?: (casterId: string, itemId: string) => boolean | null;
}
export type ChargeKind = 'adjust' | 'set' | 'inventory';
export type ChargeResolution =
  | { supported: true; kind: ChargeKind; itemId: string; before: number; after: number; delta: number }
  | { supported: false; reason: string };

const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const intLike = (v: unknown): number | null => typeof v === 'number' ? (Number.isInteger(v) ? v : null)
  : typeof v === 'string' && /^\s*-?\d+\s*$/.test(v) ? parseInt(v, 10) : null;
/** PHP is_numeric() for the scalars a compiled max can hold. */
const phpNumeric = (v: unknown): boolean => typeof v === 'number' ? Number.isFinite(v)
  : typeof v === 'string' && /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
/** PHP (int) of a numeric-or-not scalar ('NA' → 0). */
const phpInt = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : 0;
  const m = /^\s*([+-]?\d+)/.exec(String(v ?? ''));
  return phpNumeric(v) ? Math.trunc(Number(v)) : m ? parseInt(m[1]!, 10) : 0;
};
const no = (reason: string): ChargeResolution => ({ supported: false, reason });

/** ability_entry_id(): legacy flat ability_id, a bare-UUID ref, then the item_arr / entry item_id. */
export function abilityEntryId(value: unknown): string {
  if (typeof value === 'string') return upper(value);
  const entry = asRow(value);
  if (!entry) return '';
  const nested = entry.item_arr;
  const nestedId = typeof nested === 'string' ? nested : asRow(asRow(nested)?.items)?.item_id ?? asRow(nested)?.item_id;
  return upper(entry.ability_id ?? nestedId ?? asRow(entry.items)?.item_id ?? entry.item_id ?? entry.item_id);
}

/**
 * The first layer_entries() match over the charge buckets, with its compiled current / max as the page holds them. The compile elides
 * `charges.max` (the stored entry carries only `current`), and ability_entry_max_charges() then reads the DEFINITION's ability_charges
 * layer, 'NA' when it has none — mirrored through readers.definition; `maxKnown` is false when the page does not hold that definition.
 */
export function findChargeEntry(caster: Row, itemId: string, readers: ChargeReaders): { entry: Row; current: unknown; max: unknown; maxKnown: boolean } | null {
  const id = upper(itemId);
  for (const bucket of CHARGE_BUCKETS) {
    for (const value of readers.entries(caster, bucket)) {
      if (abilityEntryId(value) !== id) continue;
      const entry = asRow(value);
      if (!entry) return null; // a bare-UUID ref carries no compiled count — PHP's catalog fallback decides
      const charges = asRow(entry.charges);
      const current = charges && 'current' in charges ? charges.current : entry.ability_charges;
      if (charges && 'max' in charges) return { entry, current, max: charges.max, maxKnown: true };
      if ('max_ability_charges' in entry) return { entry, current, max: entry.max_ability_charges, maxKnown: true };
      const def = readers.definition?.(id) ?? null;
      if (!def) return { entry, current, max: undefined, maxKnown: false };
      const layer = readers.value(def, 'ability_charges');
      return { entry, current, max: layer === null || layer === undefined ? 'NA' : layer, maxKnown: true };
    }
  }
  return null;
}

/** adjust_charges(): the delta, clamped. */
export function resolveAdjustCharge(caster: Row, itemId: string, delta: unknown, readers: ChargeReaders, casterId = ''): ChargeResolution {
  const d = intLike(delta);
  if (d === null || Math.abs(d) > 100000) return no('delta-not-integer'); // validate_numeric_data owns that message
  const found = findChargeEntry(caster, itemId, readers);
  if (!found) return no('ability-entry-not-held');
  const current = intLike(found.current);
  if (current === null || current < 0) return no('current-charges-not-held');
  if (!found.maxKnown) return no('maximum-charges-not-held');
  let max = phpInt(found.max);
  if (max <= 0) max = 1; // adjust_charges(): "safety"
  if (upper(itemId) === ORB_SLOTS_ID) max = ORB_QUEUE_MAX_SLOTS;
  if (d < 0 && current > max) {
    // adjust_charges(): an upcast projectile volley persists only its COUNT, so a spend on a pool above its ceiling is bounded by the
    // pool itself (ft_projectile_pool); any other pool clamps to the ceiling. Decided from the page's pool read (N6 2026-10-01).
    const pool = readers.projectilePool?.(casterId, upper(itemId)) ?? null;
    if (pool === null) return no('projectile-pool-undecidable');
    if (pool) max = current;
  }
  const after = Math.max(0, Math.min(current + d, max));
  return { supported: true, kind: 'adjust', itemId: upper(itemId), before: current, after, delta: d };
}

/** change_charges(): the absolute value, floored at 0, clamped only by a numeric max. */
export function resolveSetCharge(caster: Row, itemId: string, value: unknown, readers: ChargeReaders): ChargeResolution {
  const v = intLike(value);
  if (v === null || Math.abs(v) > 100000) return no('value-not-integer'); // check_numeric owns that message
  const found = findChargeEntry(caster, itemId, readers);
  if (!found) return no('ability-entry-not-held');
  const current = intLike(found.current);
  if (current === null || current < 0) return no('current-charges-not-held');
  if (!found.maxKnown) return no('maximum-charges-not-held');
  let after = Math.max(0, v);
  if (phpNumeric(found.max)) after = Math.min(after, phpInt(found.max));
  return { supported: true, kind: 'set', itemId: upper(itemId), before: current, after, delta: after - current };
}

/** Every inventory row in the caster's carried tree (the inventory layer, then each container's inventory_in_container). */
export function findInventoryRow(caster: Row, inventoryId: string, readers: ChargeReaders): Row | null {
  const id = upper(inventoryId);
  const walk = (rows: unknown[], depth: number): Row | null => {
    if (depth > 12) return null;
    for (const raw of rows) {
      const row = asRow(raw);
      if (!row) continue;
      if (upper(asRow(row.inventory)?.inventory_id) === id) return row;
      const inner = Array.isArray(row.inventory_in_container) ? row.inventory_in_container : [];
      const hit = walk(inner, depth + 1);
      if (hit) return hit;
    }
    return null;
  };
  return walk(readers.entries(caster, 'inventory'), 0);
}

/** change_inventory_charges(): the absolute value clamped to [0, item_charges] (layer-aware). */
export function resolveInventoryCharge(caster: Row, inventoryId: string, value: unknown, readers: ChargeReaders): ChargeResolution {
  const v = intLike(value);
  if (v === null || Math.abs(v) > 100000) return no('value-not-integer');
  const row = findInventoryRow(caster, inventoryId, readers);
  if (!row) return no('inventory-row-not-held');
  const maxRaw = readers.value(row, 'item_charges');
  const max = intLike(maxRaw);
  if (max === null || max <= 0) return no('item-charges-not-held'); // an uncharged item: legacy owns that write
  // build_inventory_arr(): current_item_charges = the charges row, else the item_charges default.
  const currentRaw = readers.value(row, 'current_item_charges');
  const current = currentRaw === null || currentRaw === undefined ? max : intLike(currentRaw);
  if (current === null || current < 0) return no('current-charges-not-held');
  const after = Math.max(0, Math.min(v, max));
  return { supported: true, kind: 'inventory', itemId: upper(inventoryId), before: current, after, delta: after - current };
}

/** The native writer patches all held mirrors, even when the reader prefers one bucket. */
export function setAbilityCharges(caster: Row, itemId: string, current: number, _readers: ChargeReaders): boolean {
  return setRowAbilityCharges(caster, itemId, current);
}

/** The page twin of the inventory row's compiled current_item_charges (item_layer_types). */
export function setInventoryCharges(caster: Row, inventoryId: string, current: number, readers: ChargeReaders): boolean {
  const row = findInventoryRow(caster, inventoryId, readers);
  if (!row) return false;
  const layers = asRow(row.item_layer_types) ?? (row.item_layer_types = {}) as Row;
  layers.current_item_charges = current;
  return true;
}
